import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid2,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  DialogActions,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Stepper,
  StepLabel,
  Step,
  Container,
  TableContainer,
  Divider,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackError } from '../../Reducers/snackErrors';
import API from '../../api/api';
import DeleteConfirmationDialog from "../Dialogs/ConfirmDelete";

import QRCode from 'qrcode'
import { ASSESSMENT_SESSION_ID, AWS_LINK, DEV_LINK, GRADER, SESSIONS } from '../../Static/static';
import AssessmentQuestions from './AssessmentQuestions';
import { useParams } from 'react-router';

import {
  PlayArrow,
  StopCircle,
  CloudSyncRounded,
  Delete,
  QrCodeScanner as QrCodeScannerIcon,
  Spellcheck as SpellcheckIcon,
  TaskAlt as TaskAltOutlined,
  ContentCopy,
  Link as LinkIcon,
  NewReleases,
} from "@mui/icons-material";
import { useGetStudents } from '../../Hooks/useGetStudents';
import StudentsTable from '../Table/StudentsTable';
import { setSuccessSnack } from '../../Reducers/snackSuccess';
 
// ─── Status helpers ──────────────────────────────────────────────────────────
 
function getStatus(value) {
  if (!value.is_active) return "inactive";
  if (value.completed && value.grade_assessment) return "grading";
  if (value.completed) return "completed";
  return "testing";
}
 
const STATUS_CONFIG = {
  testing:   { label: "In progress",     color: "success" },
  grading:   { label: "Ready to grade",  color: "info"    },
  completed: { label: "Completed",        color: "error"   },
  inactive:  { label: "Inactive",         color: "default" },
};
 
function StatusChip({ value }) {
  const status = getStatus(value);
  const { label, color } = STATUS_CONFIG[status];
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{ fontSize: 11, height: 22, fontWeight: 500 }}
    />
  );
}

const AVATAR_COLORS = [
  { bg: "#bbdefb", color: "#1565c0" },
  { bg: "#c8e6c9", color: "#1b5e20" },
  { bg: "#ffe0b2", color: "#bf360c" },
  { bg: "#e1bee7", color: "#4a148c" },
  { bg: "#b2ebf2", color: "#006064" },
];
 
function StudentAvatar({ firstName, lastName, index = 0 }) {
  const initials = `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
  const { bg, color } = AVATAR_COLORS[index % AVATAR_COLORS.length];
  return (
    <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 500, bgcolor: bg, color }}>
      {initials}
    </Avatar>
  );
}
 
// ─── Step labels ─────────────────────────────────────────────────────────────
 
const STEPS = ["Start", "Share links", "Complete"];

export default function AddAssessmentSession({ open, onClose, substitute_id }) {


  const url = useParams();
  const role = useSelector((state) => state.role);
  const dispatch = useDispatch();


  const [studentSessions, setStudentSessions] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);

  const [selectUrl, setSelectUrl] = useState(null);
  const [openAssessmentGrader, setOpenAssessmentGrader] = useState(false);
  const [grader, setGrader] = useState({student_id: null, assessment_id: null, sessionId: null});
  
  const [sessionId, setSessionId] = useState(null);
  const [reload, setReload] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const [copied, setCopied] = useState(false);
 
  // Derive stepper position
  const activeStep = !sessionId ? 0 : showQR ? 1 : 2;
 
  const joinUrl = "https://presentifyclone.click/join";
 
  function handleCopy() {
    navigator.clipboard.writeText(joinUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }


  const closeAssessmentGrader = () => {
    setOpenAssessmentGrader(false);
    setGrader({student_id: null, assessment_id: null});
  }

  const openAssessmentGraderDialog = (student_id, assessment_id) => {    
    setGrader({student_id, assessment_id, sessionId: sessionId} );
    setOpenAssessmentGrader(true);
  }

  useEffect(() => {
    getLiveSessions();
    return () => {
      setReload(false);
      setShowQR(false);
    }
  },[reload, sessionId]);

  const getLiveSessions = () => {
    let session_id = sessionStorage.getItem('assessment_session_id');
    if (!session_id || session_id === undefined) { return;}
    if (session_id) {
      setSessionId(session_id); 
      getLiveAssessmentSessions(session_id);
    }
  }
  const [qrImage, setQrImage] = useState(null);


  const viewAssessmentQR = async (student_id, assessment_id) => {
    if (sessionId) {
      const LINK = process.env.NODE_ENV === "production" ?  "https://presentifyclone.click": "http://localhost:3000";
      const URL = LINK + `/assessment/${sessionId}/${student_id}/${assessment_id}`;
      try {
        const canvas = await generateQR(URL);
        if (canvas) {
          setShowQR(true);
          const dataURL = canvas.toDataURL()
          setQrImage(dataURL); // use React state
          setSelectUrl(URL);
        }
      } catch (error) {
        throw new Error('view assessment qr');
      }
    }
  }

  const generateQR = async (text) => {
    const canvas = document.createElement('canvas');
    try {
      QRCode.toCanvas(canvas, text, [{color: {
        dark:"#010599FF",
        light:"#FFBF60FF"
      }}]);
      return canvas
    } catch (err) {
      console.log(err)
      return null
    }
  }

  const closeDialog = () => {
    setSelectUrl(null);
    setShowQR(false);
    onClose();
  }

  const getLiveAssessmentSessions = async (session_id) => {
    try {
      if (!session_id) { return;}
      let tutor_id = role.id;
      if (substitute_id) { tutor_id = substitute_id; }
      const response = await API.get('/get_student_assessment_sessions', { params: {session_id: session_id, tutor_id}});
      if (response.data.data === null) { setStudentSessions([]);}
      if (response.data.data) { setStudentSessions(response.data.data);}
      
    }catch(error) {
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to create session.'
          }));
        }
      return;
  }
  }

  const startAssessmentSessions = async () => {
    try {
      let tutor_id = role.id;
      let ss = []
      if (substitute_id) { tutor_id = substitute_id; }
      if (selectedStudents) {
        for (const key in selectedStudents) {
          if (selectedStudents[key].easy_score === true && selectedStudents[key].assessment_id !== null) {
            ss.push({ ...selectedStudents[key], tutor_id})
          }
        }
      }
      if (ss.length === 0) { return; }
      const response = await API.post('/create_assessment_sessions', { students: ss });
      if (response.data.status === "OK") {
        if (response.data.session_id) {
          setSessionId(response.data.session_id);
          sessionStorage.setItem('assessment_session_id', response.data.session_id);
          await getLiveAssessmentSessions(sessionId);
        }
      }
    }
    catch(error) {
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        console.log(error)
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to create session.'
          }));
        }
      return;
    }
  }

  const removeSession = async (id) => {
    try {
      const response = await API.post('/delete_student_session', {session_id: sessionId, student_id: id})
      if (response.data.status === "Deleted") {
        dispatch(setSuccessSnack({title: "Delete", body: "Successfully deleted session."}));
        return;
      }
    }
    catch(error) {
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        console.log(error)
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to create session.'
          }));
        }
      return;
    }
    finally {
      getLiveSessions();
    }
  }

  const [confirmDelete, setConfirmDelete] = useState({id: null, open: false, confirm: false});

  const confirmCloseSession = (id) => {
    setConfirmDelete(() => ({id: id, open: true }));
    setSessionId(id)
  }

  const closeConfirmDelete = () => {
    setConfirmDelete({id: null, open: false});
  }

  const clearAssessmentSessions = async () => {
    try {
      let tutor_id = null;
      if (substitute_id) { tutor_id = substitute_id; }
      
      const response = await API.post('/delete_assessment_sessions', { session_id: sessionId });
      if (response.data.status === "Deleted") {
        sessionStorage.removeItem(ASSESSMENT_SESSION_ID);
        sessionStorage.removeItem(SESSIONS)
        sessionStorage.removeItem(GRADER);
        setSessionId(null);
      }
    }
    catch(error) {
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        console.log(error)
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to create session.'
          }));
        }
      return;
    }
    finally {
      closeConfirmDelete();
    }
  }

  const checkCurrentAssessmentSessions = () => {
    return selectedStudents.length === 0
  }
  const handleStudentSelect = (student) => {
    setSelectedStudents((prev) => [...prev, student]);
  };

  const handleClose = () => {
    sessionStorage.removeItem(ASSESSMENT_SESSION_ID);
    setSessionId(null);
    setSelectedStudents([]);
    onClose();
  }



  return (
    <section>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={500}>
          Assessment session
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Students can complete their test on their own device or manual entry.
        </Typography>
      </DialogTitle>
 
      {/* ── Progress stepper ── */}
      <Box sx={{ px: 3, pb: 1.5 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                slotProps={{
                  label: { style: { fontSize: 12, marginTop: 4 } },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
 
      <Divider />
 
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
 
          {/* ── Active session banner ── */}
          {sessionId && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                bgcolor: "primary.50",
                border: "1px solid",
                borderColor: "primary.200",
                borderRadius: 1,
                px: 2,
                py: 1.25,
                bgcolor: (t) => t.palette.mode === "dark" ? "primary.900" : "#e3f2fd",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "primary.main" }}
                >
                  Active session
                </Typography>
                <Typography variant="subtitle1" fontWeight={500} color="primary.dark" sx={{ lineHeight: 1.2 }}>
                  {sessionId}
                </Typography>
              </Box>
 
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Chip
                  icon={<LinkIcon sx={{ fontSize: "14px !important" }} />}
                  label={joinUrl}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontSize: 12 }}
                />
                <Tooltip title={copied ? "Copied!" : "Copy join link"}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ContentCopy sx={{ fontSize: "14px !important" }} />}
                    onClick={handleCopy}
                    disableElevation
                    sx={{ fontSize: 12, textTransform: "none", minWidth: 0 }}
                  >
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                </Tooltip>
              </Stack>
            </Box>
          )}
 
          {/* ── Action buttons ── */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
            <Tooltip title="Create a unique QR code for each student to access this assessment online" placement="top">
              <span>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={startAssessmentSessions}
                  disabled={checkCurrentAssessmentSessions() || sessionId}
                  disableElevation
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Start session
                </Button>
              </span>
            </Tooltip>
 
            {sessionId && (
              <Tooltip title="Cancel all active sessions — no assessments will be saved" placement="top">
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopCircle />}
                  onClick={() => confirmCloseSession?.(sessionId)}
                  disableElevation
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Cancel session
                </Button>
              </Tooltip>
            )}
 
            {sessionId && (
              <Button
                variant="text"
                color="info"
                startIcon={<CloudSyncRounded />}
                onClick={() => setReload?.(true)}
                sx={{ textTransform: "none" }}
              >
              </Button>
            )}
            
          </Stack>
          {sessionId && (
              <Button
                variant="text"
                color="info"
                startIcon={<NewReleases />}
                onClick={() => handleClose()}
                sx={{ textTransform: "none" }}
              >
                Start new session
              </Button>
            )}
          </Box>

 
          {/* ── QR code display ── */}
          {showQR && qrImage && (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Scan to open assessment
              </Typography>
              <img src={qrImage} alt="Assessment QR" style={{ maxWidth: 200, marginTop: 6 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {selectUrl}
              </Typography>
            </Box>
          )}
 
          {/* ── Student table ── */}
          {sessionId && (
            <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40, bgcolor: "grey.50", py: 1 }} />
                    <TableCell sx={{ bgcolor: "grey.50", py: 1, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>
                      Student
                    </TableCell>
                    <TableCell sx={{ bgcolor: "grey.50", py: 1, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ bgcolor: "grey.50", py: 1, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>
                      Grade
                    </TableCell>
                    <TableCell sx={{ bgcolor: "grey.50", py: 1, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>
                      Join code
                    </TableCell>
                    <TableCell sx={{ width: 40, bgcolor: "grey.50", py: 1 }} />
                  </TableRow>
                </TableHead>
 
                <TableBody>
                  {studentSessions.map((value, idx) => (
                    <TableRow key={value.id} hover>
                      {/* Delete */}
                      <TableCell sx={{ px: 0.5 }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeSession?.(value.id)}
                          sx={{ "&:hover": { bgcolor: "error.50" } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
 
                      {/* Name */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <StudentAvatar firstName={value.first_name} lastName={value.last_name} index={idx} />
                          <Typography variant="body2" fontSize={13} noWrap>
                            {`${value.first_name} ${value.last_name}`}
                          </Typography>
                        </Stack>
                      </TableCell>
 
                      {/* Status */}
                      <TableCell>
                        <StatusChip value={value} />
                      </TableCell>
 
                      {/* Grade */}
                      <TableCell>
                        {value.grade_assessment ? (
                          <Tooltip title="Needs manual grading" placement="left">
                            <IconButton
                              size="small"
                              onClick={() => openAssessmentGraderDialog?.(value.id, value.assessment_id)}
                            >
                              <SpellcheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Auto-grade enabled" placement="left">
                            <TaskAltOutlined fontSize="small" color="success" />
                          </Tooltip>
                        )}
                      </TableCell>
 
                      {/* Join code */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontSize={12}
                          fontFamily="monospace"
                          sx={{
                            bgcolor: "grey.100",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 0.5,
                            px: 0.75,
                            py: 0.25,
                            display: "inline-block",
                            letterSpacing: "0.06em",
                          }}
                        >
                          {value.join_code}
                        </Typography>
                      </TableCell>
 
                      {/* QR */}
                      <TableCell sx={{ px: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => viewAssessmentQR?.(value.id, value.assessment_id)}
                        >
                          <QrCodeScannerIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
 
                  {studentSessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary", fontSize: 13 }}>
                        No students in this session yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
 
          {!sessionId && <StudentsTable onSelectAction={handleStudentSelect} />}
 
        </Stack>
      </DialogContent>
 
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={closeDialog} variant="text" color="inherit" sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
      <AssessmentQuestions 
        open={openAssessmentGrader} 
        onClose={closeAssessmentGrader} 
        proctor 
        grader 
        assessmentId={grader.assessment_id} 
        studentId={grader.student_id}
        sessionId={grader.sessionId}
        
      />
      <DeleteConfirmationDialog open={confirmDelete.open} onClose={closeConfirmDelete} onConfirm={clearAssessmentSessions}
       />

    </section>
  );
}

